import { IconButton } from '@nexelem/components';
import { forwardRef, type SyntheticEvent, useMemo } from 'react';
import type { DISPLAY_FIELD } from '../types';
import { NodeItemListOverlay } from './NodeItemListOverlay';
import { getTaskStatusColor } from '../utils';
import { ToDoCheckbox } from './ToDoCheckbox';
import { NodeIcon } from '../../ProductionPlans/Gantt/components';
import { getPossibleQueues } from '../../ProductionPlans/Gantt/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDateFormatter, cn } from '@nexelem/utils';
import { useGanttDraggable } from '../../ProductionPlans/Gantt/hooks';
import { NodeType } from '../../ProductionPlans/Gantt/types';
import { useIsGanttSimulation } from '../../ProductionPlans/GanttSimulation/hooks';
import { useProductionPlanSettings } from '../../ProductionPlans/components/SettingsDrawer';
import { useCustomTranslation } from '../../../../hooks';
import type { TaskToDoEntry } from '@nexelem/graphne-api';
import { faEllipsis } from '@fortawesome/pro-light-svg-icons';
import { ProductionOrderNumber } from '../../ProductionPlans/Gantt/components';

export type FlatListNodeItemProps = {
  task: TaskToDoEntry;
  isChecked: boolean;
  onMoreClick: (e: SyntheticEvent, task: TaskToDoEntry) => void;
  setChecked: (evt: SyntheticEvent) => void;
  isDragDisabled?: boolean;
  displayField: DISPLAY_FIELD;
  isBatchChild?: boolean;
};

export const FlatListNodeItem = forwardRef<HTMLDivElement, FlatListNodeItemProps>(
  (
    {
      task,
      onMoreClick,
      setChecked,
      isChecked,
      isDragDisabled = false,
      displayField,
      isBatchChild = false,
    }: FlatListNodeItemProps,
    ref,
  ) => {
    const isSimulation = useIsGanttSimulation();
    const showProductionOrderNumber = useProductionPlanSettings(
      (state) => state.gantt.showOnTask.productionOrderNumber,
    );
    const { tWrapper } = useCustomTranslation('products.productionPlan.todoList');
    const dateFormatter = useDateFormatter({ displayWithHours: true });

    const { attributes, listeners, setNodeRef, isDragging, over } = useGanttDraggable({
      disabled: isDragDisabled,
      id: task.id,
      data: {
        id: task.id,
        duration: task?.estimatedDurations?.median,
        possibleQueues: getPossibleQueues(task),
        type: NodeType.TASK,
      },
    });

    const internalClickMore = (e: SyntheticEvent) => onMoreClick(e, task);
    const taskStatusColor = useMemo(() => getTaskStatusColor(task.status) || 'grey', [task.status]);

    return (
      <div className="node-container relative w-full min-w-full" ref={ref}>
        <div
          ref={setNodeRef}
          className={cn(
            'cursor-pointer border-b border-border-secondary transition-colors hover:bg-surface-focus-lighter',
            isChecked && 'bg-surface-focus-lighter',
            isBatchChild && 'bg-surface-container-lighter',
          )}
          {...listeners}
          {...attributes}
        >
          <div
            className={cn(
              'flex items-center gap-0.5 py-3 pr-1',
              isBatchChild ? 'pl-15' : isSimulation ? 'pl-1' : 'pl-2',
            )}
          >
            {isSimulation && !isBatchChild && (
              <div className="flex items-center justify-center">
                <ToDoCheckbox checked={isChecked} onChange={setChecked} />
              </div>
            )}
            <div className="horizontal-dash">
              <NodeIcon
                dueDate={task.dueDate ? new Date(task.dueDate) : undefined}
                statusColor={taskStatusColor}
                taskType={task.taskType}
              />
            </div>
            <div className="ml-3 flex flex-col gap-1 text-s11-sb uppercase">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'overflow-hidden text-s11-sb text-ellipsis whitespace-nowrap',
                    getDisplayFieldMaxWidth({ showProductionOrderNumber, isSimulation, isBatchChild }),
                  )}
                >
                  {task[displayField]}
                </span>
                <ProductionOrderNumber productionOrderNumber={task.productionOrderDetails?.number} />
              </div>
              <div className="flex gap-2 text-text-secondary-darker">
                <span>
                  {task.totalAmount?.amount} {task.totalAmount?.unit.name}
                </span>
                {task.dueDate && (
                  <span>
                    {tWrapper('dueDate')} {dateFormatter(task.dueDate)}
                  </span>
                )}
              </div>
            </div>
            <IconButton
              className="ml-auto"
              Icon={<FontAwesomeIcon icon={faEllipsis} />}
              size="small"
              onClick={internalClickMore}
            />
          </div>
        </div>
        {isDragging && <NodeItemListOverlay over={over} task={task} />}
      </div>
    );
  },
);

FlatListNodeItem.displayName = 'NodeItemList';

type DisplayFieldMaxWidthArgs = {
  showProductionOrderNumber: boolean;
  isSimulation: boolean;
  isBatchChild: boolean;
};

const getDisplayFieldMaxWidth = ({
  showProductionOrderNumber,
  isSimulation,
  isBatchChild,
}: DisplayFieldMaxWidthArgs) => {
  if (showProductionOrderNumber) {
    if (isSimulation) return isBatchChild ? 'max-w-32' : 'max-w-40';
    return isBatchChild ? 'max-w-40' : 'max-w-48';
  }

  if (isSimulation) return isBatchChild ? 'max-w-44' : 'max-w-62';
  return isBatchChild ? 'max-w-61' : 'max-w-74';
};
